<template>
  <div :class="wrapperClasses">
    <label v-if="label" :for="inputId" class="ui-input__label">
      {{ label }}
      <span v-if="required" class="ui-input__required">*</span>
    </label>
    <div class="ui-input__container">
      <div v-if="slots.leading || leadingIcon || leadingIconSrc" class="ui-input__leading">
        <img v-if="leadingIconSrc" :src="leadingIconSrc" class="ui-input__icon-img" />
        <Icon v-else-if="leadingIcon" :icon="leadingIcon" class="ui-input__icon" />
        <slot v-else name="leading"></slot>
      </div>

      <input
        :id="inputId"
        ref="inputRef"
        :type="type"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :class="inputClasses"
        v-bind="$attrs"
        @input="handleInput"
        @blur="handleBlur"
        @focus="handleFocus"
      />

      <div
        v-if="slots.trailing || trailingIcon || trailingIconSrc || clearable"
        class="ui-input__trailing"
      >
        <button
          v-if="clearable && modelValue"
          type="button"
          class="ui-input__clear"
          @click="handleClear"
          tabindex="-1"
        >
          <Icon icon="mdi:close" />
        </button>
        <img v-else-if="trailingIconSrc" :src="trailingIconSrc" class="ui-input__icon-img" />
        <Icon v-else-if="trailingIcon" :icon="trailingIcon" class="ui-input__icon" />
        <slot v-else name="trailing"></slot>
      </div>
    </div>
    <div v-if="error || helperText" class="ui-input__helper">
      <span v-if="error" class="ui-input__error">{{ error }}</span>
      <span v-else-if="helperText" class="ui-input__helper-text">{{ helperText }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Icon } from "@iconify/vue";
import { computed, ref, useSlots } from "vue";
import type { Size } from "./types";

const slots = useSlots();

interface Props {
  modelValue?: string | number;
  label?: string;
  type?:
    | "text"
    | "email"
    | "password"
    | "number"
    | "tel"
    | "url"
    | "search"
    | "date"
    | "time"
    | "datetime-local";
  placeholder?: string;
  size?: Size;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  error?: string;
  helperText?: string;
  leadingIcon?: string;
  leadingIconSrc?: string;
  trailingIcon?: string;
  trailingIconSrc?: string;
  clearable?: boolean;
  id?: string;
}

const props = withDefaults(defineProps<Props>(), {
  type: "text",
  size: "md",
  disabled: false,
  readonly: false,
  required: false,
  clearable: false,
});

const emit = defineEmits<{
  "update:modelValue": [value: string];
  blur: [event: FocusEvent];
  focus: [event: FocusEvent];
}>();

const isFocused = ref(false);
const inputId = computed(() => props.id || `input-${Math.random().toString(36).substr(2, 9)}`);

const wrapperClasses = computed(() => {
  const classes = ["ui-input"];
  classes.push(`ui-input--${props.size}`);
  if (props.disabled) classes.push("ui-input--disabled");
  if (props.error) classes.push("ui-input--error");
  return classes;
});

const inputClasses = computed(() => {
  const classes = ["ui-input__field"];
  if (props.leadingIcon || props.leadingIconSrc || slots.leading) {
    classes.push("ui-input__field--has-leading");
  }
  if (props.trailingIcon || props.trailingIconSrc || slots.trailing || props.clearable) {
    classes.push("ui-input__field--has-trailing");
  }
  if (isFocused.value) classes.push("ui-input__field--focused");
  return classes;
});

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit("update:modelValue", target.value);
};

const handleBlur = (event: FocusEvent) => {
  isFocused.value = false;
  emit("blur", event);
};

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true;
  emit("focus", event);
};

const handleClear = () => {
  emit("update:modelValue", "");
};

const inputRef = ref<HTMLInputElement | null>(null);

defineExpose({
  focus: () => inputRef.value?.focus(),
});
</script>

<style scoped>
.ui-input {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.ui-input__label {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #344054;
  margin-bottom: 6px;
}

.ui-input__required {
  color: var(--color-danger);
  font-size: 1.1rem;
  margin-left: 2px;
}

.ui-input__container {
  position: relative;
  display: flex;
  align-items: center;
}

.ui-input__field {
  width: 100%;
  font-family: var(--font-family);
  font-size: var(--font-size-sm);
  font-weight: 400;
  color: var(--color-text);
  background-color: var(--color-bg-white);
  border: 1px solid var(--color-border);
  border-radius: calc(var(--radius) - 2px);
  padding: 10px 12px;
  transition: all var(--transition-normal);
  height: var(--input-height-md);
}

.ui-input__field::placeholder {
  color: var(--color-text-muted);
  font-weight: 400;
  font-size: var(--font-size-sm);
}

.ui-input__field:hover:not(:disabled) {
  border-color: var(--color-border-hover);
}

.ui-input__field:focus {
  outline: none !important;
  border-color: var(--color-primary) !important;
  box-shadow: var(--ring) !important;
}

.ui-input__field:disabled {
  background-color: var(--color-bg-disabled);
  cursor: not-allowed;
  opacity: 0.5;
  border-color: var(--color-border-light);
}

.ui-input__field--has-leading {
  padding-left: 40px;
}

.ui-input__field--has-trailing {
  padding-right: 40px;
}

.ui-input__leading,
.ui-input__trailing {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  pointer-events: none;
}

.ui-input__leading {
  left: 12px;
}

.ui-input__trailing {
  right: 12px;
}

.ui-input__icon {
  font-size: 1rem;
  color: var(--color-text-placeholder);
  transition: color var(--transition-normal);
}

.ui-input__field:focus ~ .ui-input__leading .ui-input__icon,
.ui-input__field:focus ~ .ui-input__trailing .ui-input__icon {
  color: var(--color-primary);
}

.ui-input__icon-img {
  height: 16px;
  width: 16px;
  object-fit: contain;
}

.ui-input__clear {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  pointer-events: all;
  color: var(--color-text-placeholder);
  border-radius: 4px;
  transition: all var(--transition-normal);
  opacity: 0.7;
}

.ui-input__clear:hover {
  background-color: var(--color-bg-muted);
  color: var(--color-text);
  opacity: 1;
}

.ui-input__clear .iconify {
  font-size: 0.75rem;
}

.ui-input__helper {
  margin-top: var(--space-1);
  font-size: var(--font-size-xs);
}

.ui-input__error {
  color: var(--color-danger);
}

.ui-input__helper-text {
  color: var(--color-text-placeholder);
}

/* Size: sm */
.ui-input--sm .ui-input__label {
  font-size: 12px;
}

.ui-input--sm .ui-input__field {
  height: var(--input-height-sm);
  padding: 8px 10px;
  font-size: var(--font-size-sm);
}

.ui-input--sm .ui-input__field::placeholder {
  font-size: var(--font-size-sm);
}

.ui-input--sm .ui-input__field--has-leading {
  padding-left: 36px;
}

.ui-input--sm .ui-input__field--has-trailing {
  padding-right: 36px;
}

/* Size: lg */
.ui-input--lg .ui-input__field {
  height: var(--input-height-lg);
  padding: 12px 16px;
  font-size: var(--font-size-sm);
}

.ui-input--lg .ui-input__field::placeholder {
  font-size: var(--font-size-sm);
}

.ui-input--lg .ui-input__field--has-leading {
  padding-left: 44px;
}

.ui-input--lg .ui-input__field--has-trailing {
  padding-right: 44px;
}

/* Error state */
.ui-input--error .ui-input__field {
  border-color: var(--color-danger);
}

.ui-input--error .ui-input__field:focus {
  border-color: var(--color-danger) !important;
  box-shadow: var(--ring-danger) !important;
}

/* Date/time inputs */
.ui-input__field[type="date"],
.ui-input__field[type="time"],
.ui-input__field[type="datetime-local"] {
  border: 1px solid var(--color-border) !important;
  -webkit-appearance: none;
  -moz-appearance: textfield;
  cursor: pointer;
}

.ui-input__field[type="date"]:focus,
.ui-input__field[type="time"]:focus,
.ui-input__field[type="datetime-local"]:focus {
  border-color: var(--color-primary) !important;
  box-shadow: var(--ring) !important;
}

.ui-input__field[type="date"]:hover:not(:disabled),
.ui-input__field[type="time"]:hover:not(:disabled),
.ui-input__field[type="datetime-local"]:hover:not(:disabled) {
  border-color: var(--color-border-hover) !important;
}
</style>
